import React, { useRef } from 'react'
import {
  Grid,
  Typography,
  Modal,
  Box,
  Chip,
  IconButton,
  useMediaQuery,
  useTheme,
  Button,
  styled,
} from '@mui/material'
import LocationOnIcon from '@mui/icons-material/LocationOn'
import CloseIcon from '@mui/icons-material/Close'
import { buildApptURL } from '../Avatar'
import { ProviderListItem } from '../../utils/hooks'
import {
  ModalButton,
  ModalContainer,
  ModalImage,
  NoScrollbar,
  StyledButtonContainer,
} from './styles'

type ProviderModalProps = {
  onModalToggle: (provider: ProviderListItem) => void
  openModal: boolean
  setOpen: (status: boolean) => void
  selectedProvider: ProviderListItem
  trackOnSchedule: () => void
}

const DotSeparator = styled('span')(({ theme }) => ({
  marginLeft: theme.spacing(1),
  marginRight: theme.spacing(1),
}))

const ProviderModal = ({
  onModalToggle,
  openModal,
  setOpen,
  selectedProvider,
  trackOnSchedule,
}: ProviderModalProps) => {
  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'))
  const isDesktop = useMediaQuery(theme.breakpoints.up('lg'))
  const chipsRef = useRef<HTMLDivElement | null>(null)
  const bioWrapperRef = useRef<HTMLDivElement | null>(null)

  const renderLanguages = () => {
    const elements: React.ReactNode[] = []

    selectedProvider.languages.forEach((language, index) => {
      elements.push(<span key={language}>{language}</span>)
      if (index < selectedProvider.languages.length - 1) {
        elements.push(
          <DotSeparator key={`${language}-separator`}>•</DotSeparator>
        )
      }
    })

    return elements
  }

  return (
    <Box>
      <Modal
        disableAutoFocus
        disablePortal
        open={openModal}
        onClose={onModalToggle}
        aria-labelledby="provider-screen"
        aria-describedby="provider-screen-description"
        BackdropProps={{
          sx: {
            backgroundColor: 'transparent',
            backdropFilter: 'blur(2px)',
            width: '100%',
          },
        }}
      >
        <NoScrollbar>
          <ModalContainer id="modal-content">
            <IconButton
              edge="end"
              color="inherit"
              onClick={() => setOpen(false)}
              aria-label="close"
              sx={{
                position: 'absolute',
                top: 10,
                right: 30,
                zIndex: 9999,
              }}
            >
              <CloseIcon />
            </IconButton>
            <Grid container spacing={2}>
              <Grid item lg={4} xs={12}>
                <Box
                  style={{
                    width: '95%',
                    margin: 'auto',
                    [theme.breakpoints.down('sm')]: {
                      marginTop: '-50px',
                    },
                  }}
                >
                  <ModalImage
                    src={selectedProvider.image}
                    alt={selectedProvider.slug}
                    sx={{
                      marginTop: '10px',
                      marginBottom: isMobile ? '0' : '20px',
                    }}
                  />
                  {!isMobile && (
                    <ModalButton
                      onClick={trackOnSchedule}
                      href={buildApptURL(
                        selectedProvider.specialtyAreaKeys[0],
                        selectedProvider.slug
                      )}
                      className="modalBtn"
                    >
                      <Box 
                        sx={{ 
                          width: '100%', 
                          display: 'flex', 
                          justifyContent: 'center', 
                          p: 0, 
                        }}
                      >
                        <Button
                        sx={{
                          whiteSpace: 'nowrap',
                          width: '100%',
                          textTransform: 'capitalize'
                        }}
                          variant="contained"
                        >
                        <Typography color='white' fontWeight='medium' fontSize={20}>Schedule Appointment</Typography>
                        </Button>
                      </Box>
                    </ModalButton>
                  )}
                </Box>
              </Grid>

              <Grid item lg={8} xs={12}>
                <Box
                  sx={{
                    padding: '10px',
                    maxHeight: isDesktop ? '35rem' : 'none',
                  }}
                >
                  <Typography variant="h4" fontWeight={600} id="modal-title">
                    {selectedProvider.displayName}
                  </Typography>
                  <Typography variant="h6" color="primary">
                    {selectedProvider.specialtyAreaNames}
                  </Typography>
                  <Typography
                    variant="subtitle1"
                    style={{ color: 'grey', marginTop: '10px' }}
                  >
                    <span>Ages {selectedProvider.ageRange}</span>
                    <DotSeparator>•</DotSeparator>
                    {renderLanguages()}
                  </Typography>
                  {selectedProvider.locations &&
                    selectedProvider.locations.length > 0 && (
                      <div style={{ display: 'flex', alignItems: 'center' }}>
                        <LocationOnIcon
                          style={{ color: 'grey', marginLeft: '-5px' }}
                        />
                        <Typography
                          variant="subtitle1"
                          component="p"
                          display="inline"
                          style={{ color: 'grey' }}
                        >
                          {selectedProvider.locations.map((l, index) => (
                            <React.Fragment key={l}>
                              <span>{l}</span>
                              {index < selectedProvider.locations.length - 1 &&
                                ', '}
                            </React.Fragment>
                          ))}
                        </Typography>
                      </div>
                    )}
                  <Box
                    ref={bioWrapperRef}
                    className="bioWrapper"
                    style={{
                      marginTop: '15px',
                      position: 'relative',
                      paddingBottom: isMobile ? '0' : '3rem',
                      overflowY: 'auto',
                    }}
                  >
                    <div>
                      {selectedProvider.bio.split(/\n{1,2}/).map((p) => (
                        <React.Fragment key={p}>
                          <Typography
                            style={{ fontSize: '21px' }}
                            variant="body1"
                          >
                            {p}
                          </Typography>
                          <br />
                        </React.Fragment>
                      ))}
                    </div>
                  </Box>
                  {isMobile && (
                    <StyledButtonContainer className="btnWrapper">
                      <ModalButton
                        href={buildApptURL(
                          selectedProvider.specialtyAreaKeys[0],
                          selectedProvider.slug
                        )}
                        className="modalBtn"
                      >
                        <Button
                          variant="contained"
                        >
                          <Typography fontWeight='medium' fontSize={22}>Schedule Appointment</Typography>
                        </Button>
                      </ModalButton>
                    </StyledButtonContainer>
                  )}
                  <Box
                    style={{
                      marginTop: isMobile ? '20px' : '-40px',
                      marginBottom: '40px',
                      paddingBottom: '20px',
                    }}
                  >
                    {selectedProvider.specialties.map((s) => (
                      <Chip
                        ref={chipsRef}
                        className="chips"
                        key={s}
                        variant="outlined"
                        color="primary"
                        sx={{
                          color: theme.palette.primary.main,
                          margin: '4px',
                          fontSize: '20px',
                        }}
                        label={s}
                      />
                    ))}
                  </Box>
                </Box>
              </Grid>
            </Grid>
          </ModalContainer>
        </NoScrollbar>
      </Modal>
    </Box>
  )
}

export default ProviderModal
